import DOMPurify from "dompurify";
import React from "react";

interface ISafeHTMLProps<P extends React.HTMLAttributes<T>, T extends HTMLElement> {
    element?: keyof React.ReactHTML;
    html: string;
    options?: {
        allowedTags: string[];
        allowedSchemes: string[];
    };
    className?: string;
    props?: React.ClassAttributes<T> & P;
    children?: React.ReactNode | undefined;
}

/**
 * Uses DOMPurify to sanitize HTML content before rendering it.
 * Replaces new lines with break tags.
 */
export class SafeHTML<
    P extends React.HTMLAttributes<T>,
    T extends HTMLElement,
> extends React.Component<ISafeHTMLProps<P, T>, unknown> {
    render() {
        const {
            element = "span",
            options = {
                allowedTags: ["a", "br"],
                allowedSchemes: ["tel", "mailto", "https"],
            },
            html,
            className,
            ...props
        } = this.props;

        const config: DOMPurify.Config = {
            ALLOWED_TAGS: options.allowedTags,
            ALLOWED_ATTR: ["href"],
        };

        if (options.allowedSchemes.length > 0) {
            const schemesPattern = options.allowedSchemes.join("|");
            config.ALLOWED_URI_REGEXP = new RegExp(`^(${schemesPattern}):`);
        }

        const sanitizedHtml = DOMPurify.sanitize(html, config);
        const sanitizedWithLineBreaks = sanitizedHtml.includes("<br")
            ? sanitizedHtml
            : sanitizedHtml.replace(/\n/g, "<br />");

        return React.createElement(element, {
            dangerouslySetInnerHTML: { __html: sanitizedWithLineBreaks },
            className: `textFromSettings ${className ? className : ""}`,
            ...props,
        });
    }
}
